// src/components/vendors/SendVendorToClientModal.jsx
// "Send to Client": email a Vendor Directory vendor's info to a client.
// Recipient comes from the agent's Contacts (picker) or a typed email;
// optional personal note is woven into the branded email.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import AddClientPicker from "../new-contact/AddClientPicker";
import { getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import { buildVendorEmailContent } from "../../utils/vendorEmail";
import { sendVendorEmail, openMailtoFallback } from "../../utils/emailSender";

const looksLikeEmail = (v) => /\S+@\S+\.\S+/.test(String(v || "").trim());

export default function SendVendorToClientModal({ open, vendor, onClose }) {
  const [{ user }] = useStateValue();
  const [to, setTo] = useState("");
  const [toName, setToName] = useState("");
  const [note, setNote] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo("");
    setToName("");
    setNote("");
    setPickerOpen(false);
  }, [open]);

  if (!open || !vendor) return null;

  const pickContact = async (contactId) => {
    setPickerOpen(false);
    try {
      const [raw] = await getContactsByIds([contactId]);
      const c = normalizeContact(raw);
      if (!c?.email) {
        toast.warn(`${c?.name || "That contact"} has no email on file. Type one below.`);
        setToName(c?.name || "");
        return;
      }
      setTo(c.email);
      setToName(c.name || "");
    } catch (err) {
      console.error("Contact lookup failed:", err);
      toast.error("Could not load that contact. Type the email instead.");
    }
  };

  const send = async () => {
    if (!looksLikeEmail(to)) {
      toast.error("Enter the client's email address.");
      return;
    }
    setSending(true);
    const { subject, text, html } = buildVendorEmailContent({ vendor, note, toName, user });
    const r = await sendVendorEmail({
      userId: user.id,
      to: to.trim(),
      toName,
      subject,
      text,
      html,
    });
    setSending(false);
    if (r.ok) {
      toast.success(`Sent to ${toName || to.trim()}.`);
      onClose();
    } else {
      toast.error("Could not send from your connected email. Opening your mail app instead.");
      openMailtoFallback({ to: to.trim(), subject, body: text });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Send to Client</h2>
            <p className="text-xs text-slate-500">
              {[vendor.name, vendor.company].filter(Boolean).join(" · ")}
              {vendor.category ? ` (${vendor.category})` : ""}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
            To
          </label>
          <div className="flex gap-2 mb-1">
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@email.com"
              inputMode="email"
              className="flex-1 border border-slate-300 rounded-lg p-2.5 outline-none focus:border-slate-800"
            />
            <button
              onClick={() => setPickerOpen(true)}
              className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 rounded-lg whitespace-nowrap"
            >
              Choose from Contacts
            </button>
          </div>
          {toName && <p className="text-xs text-slate-500 mb-3">Sending to {toName}</p>}

          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 mt-3">
            Personal note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="This is the plumber I mentioned. Great with older homes."
            rows={3}
            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:border-slate-800 mb-4"
          />

          <button
            onClick={send}
            disabled={sending}
            className="w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold rounded-lg py-3"
          >
            {sending ? "Sending..." : "Send"}
          </button>
          <p className="text-[11px] text-slate-400 mt-2 text-center">
            Sends from your connected email with the Team Edelstein look.
          </p>
        </div>
      </div>

      {pickerOpen && (
        <AddClientPicker
          userId={user.id}
          onClose={() => setPickerOpen(false)}
          onPick={pickContact}
          onCreateNew={() => {
            setPickerOpen(false);
            toast.info("Add them in Contacts first, or just type their email here.");
          }}
        />
      )}
    </div>
  );
}
